import type { AxiosInstance } from "axios";
import { API_ROUTES } from "@/constants/apiRoutes";
import { api } from "@/services/apiClient";
import { isRecord, readString } from "@/services/apiResponseHelpers";
import type { ApiRequestOptions } from "@/services/iamApi";
import type { BillingAccount } from "@/types/billing";

/**
 * Reads the balance of the account the caller's project spends from. The
 * account is worked out from the token, so there is nothing to name in the
 * request; reading it is also what opens the account on the first read.
 */
export async function fetchBillingAccount(
  options: ApiRequestOptions = {},
  apiOverride?: AxiosInstance,
): Promise<BillingAccount | null> {
  const client = apiOverride ?? api;
  const { data } = await client.get<unknown>(
    API_ROUTES.ecosystem.billingAccount,
    options,
  );

  return normalizeBillingAccount(data);
}

// The balance is read as the text the platform wrote it as. The number beside
// it is there for anything that counts, and the header only shows the figure:
// money formatted in the browser is money the browser starts rounding.
function normalizeBillingAccount(data: unknown): BillingAccount | null {
  if (!isRecord(data)) return null;

  const balanceDisplay = readString(data.balance_display);
  if (!balanceDisplay) return null;

  return {
    balanceDisplay,
    currency: readString(data.currency) ?? "",
    uuid: readString(data.uuid) ?? "",
  };
}
